// @ts-check
/** @type { import('next-translate').I18nConfig } */
module.exports = {
    locales: ['en'], // all locales still work without being added here, but this is required for next-translate
    defaultLocale: 'en',
    // by default next-translate doesn't fallback to "en" if no translation exists. So we need to do this ourselves
    // Below will fetch whichever lang plus the english namespace and merges them together
    // https://github.com/vinissimus/next-translate/issues/463
    loadLocaleFrom: async (lang, ns) => {
        let [locales, en] = await Promise.allSettled([
            import(`./locales/${lang}/${ns}.json`),
            import(`./locales/en/${ns}.json`),
        ]);
        locales = locales.status === 'fulfilled' ? locales.value : {};
        en = en.status === 'fulfilled' ? en.value : {};
        return merge(locales, en);
    },
    // Mapping of pages and the namespaces they use,
    // during prod builds next-translate needs this mapping to inline the translations into getServerSideProps for each page
    // so they can be read from memory at runtime.
    pages: {
        '*': ['common', 'tableauToolbar'],
        '/': ['home'],
        '/_status/preview/pdf': ['pdfViewer'],
        'rgx:/account/?.*': ['account'],
        'rgx:/analyst-reactions/?.*': ['analystReactions', 'shorts', 'insights'],
        'rgx:/assets/?.*': [
            'assets',
            'dataRelatedContent',
            'powerPlants',
            'estProjects',
            'assetFinancing',
            'renewableProjects',
            'powerPurchaseAgreements',
            'hydrogen',
            'organizations',
            'windTurbines',
        ],
        'rgx:/hydrogen-assets/?.*': [
            'hydrogen',
            'dataRelatedContent',
            'estProjects',
            'assetFinancing',
            'renewableProjects',
        ],
        'rgx:/authors/?.*': ['authors'],
        'rgx:/asset-financing/?.*': [
            'dataRelatedContent',
            'assetFinancing',
            'estProjects',
            'renewableProjects',
            'hydrogen',
            'organizations',
        ],
        'rgx:/battery-assets/?.*': ['assets', 'dataRelatedContent', 'renewableProjects'],
        'rgx:/capacity/?.*': ['countryProfiles', 'capacityAndGeneration', 'dataRelatedContent'],
        'rgx:/countries/?.*': ['countryProfiles', 'capacityAndGeneration', 'dataRelatedContent', 'hydrogen'],
        'rgx:/data-and-tools': ['data'],
        'rgx:/equipment-and-financing/?.*': ['assets', 'dataRelatedContent', 'organizations', 'assetFinancing'],
        'rgx:/events/[^/]+/slides/?': ['events', 'pdfViewer'],
        'rgx:/events/?.*': ['events', 'insights'],
        'rgx:/exports/?.*': ['assets', 'myItems'],
        'rgx:/flagships/.*': ['countryProfiles'],
        'rgx:/geoeconomics/?.*': ['pdfViewer'],
        'rgx:/generation/?.*': ['countryProfiles', 'capacityAndGeneration', 'dataRelatedContent'],
        'rgx:/help/?.*': ['helpCenter'],
        'rgx:/history/?.*': ['myItems'],
        'rgx:/insights/?.*': ['news', 'shorts', 'insights'],
        'rgx:/insights-localized/?.*': ['insights'],
        'rgx:/insights/[^/]+/view/?': ['insights', 'pdfViewer'],
        'rgx:/interactive-datasets/?.*': ['pdfViewer', 'interactiveDataset'],
        'rgx:/login/?.*': ['login'],
        'rgx:/market-outlooks/?': ['marketOutlooks'],
        'rgx:/news/?.*': ['news', 'shorts', 'insights'],
        'rgx:/organizations/\\w+/storage-assets/?.*': ['organizations', 'estProjects', 'windTurbines'],
        'rgx:/organizations/?.*': ['organizations', 'windTurbines'],
        'rgx:/policies/?.*': ['dataRelatedContent', 'estProjects', 'renewableProjects'],
        'rgx:/popular/?.*': ['popular'],
        'rgx:/power-plants/?.*': ['dataRelatedContent', 'powerPlants', 'estProjects', 'assetFinancing'],
        'rgx:/power-purchase-agreements/?.*': ['dataRelatedContent', 'estProjects', 'powerPurchaseAgreements'],
        'rgx:/renewable-projects/?.*': ['renewableProjects', 'dataRelatedContent', 'estProjects', 'assetFinancing'],
        'rgx:/recommendations/?.*': ['recommendations'],
        'rgx:/saved-items/?.*': ['myItems'],
        'rgx:/saved-searches/?.*': ['myItems'],
        'rgx:/search/?.*': ['myItems'],
        'rgx:/sectors/?.*': ['sectors', 'estProjects'],
        'rgx:/series/?.*': ['series'],
        'rgx:/shorts/?.*': ['shorts', 'insights'],
        'rgx:/slides/?.*': ['events', 'pdfViewer'],
        'rgx:/solar-manufacturing-assets/?.*': ['assets', 'dataRelatedContent', 'solarManufacturingAssets'],
        'rgx:/storage-assets/?.*': [
            'dataRelatedContent',
            'estProjects',
            'assetFinancing',
            'renewableProjects',
            'hydrogen',
        ],
        'rgx:/storage-assets/role-viewer.*': ['estProjects'],
        'rgx:/summits/?.*': ['events', 'summits', 'insights'],
        'rgx:/summits/[^/]+/sessions/[^/]+/slides/?': ['pdfViewer'],
        'rgx:/themes/?.*': ['themes', 'shorts', 'insights'],
        'rgx:/videos/?.*': ['videos'],
        'rgx:/whats-new/?.*': ['whatsNew'],
        'rgx:/wind/turbine-contracts/?.*': [
            'windTurbines',
            'countryProfiles',
            'dataRelatedContent',
            'renewableProjects',
            'organizations',
        ],
        'rgx:/wind/turbines/?.*': [
            'dataRelatedContent',
            'windTurbines',
            'estProjects',
            'renewableProjects',
            'countryProfiles',
            'organizations',
        ],
        'rgx:/wind/market-shares/?.*': ['windTurbines', 'countryProfiles', 'dataRelatedContent'],
    },
    localeDetection: false,
    // The default namespace to use if non is selected when using useTranslation();
    defaultNS: 'common',
    // Quieten log
    logBuild: false,
};

/**
 * @desc - This is not comprehensive. It is only tested for translation where elements are either Object or strings
 * and both object have a similar structure.
 *
 * @param {any} a
 * @param {any} b
 * @returns {any}
 */
const merge = (a, b) => {
    const result = { ...a };
    for (const key of Object.keys(b)) {
        if (typeof a[key] === 'object' && typeof b[key] === 'object') {
            result[key] = merge(a[key], b[key]);
        } else if (a[key] === undefined) {
            result[key] = b[key];
        }
    }
    return result;
};
